import { Injectable, EventEmitter } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DashModuleBase, IDashModuleBase, DashConf } from 'starbase-dashboard';
import { DashConfSave, TimeZone } from './contact/admin-dashboard.component';
import { ContactModel, FilterParams, IUserAccountModel } from 'starbase-launcher';
import { Observable, of, Subject } from 'rxjs';
import { share } from 'rxjs/operators';
import { PasswordScoreEnum } from 'account-management';
import { LU_SubscriptionPriceModel_ext } from './models/lu_subscriptionprice';
import { CallNumberModel } from './models/callNumberModel';
import { ImageFile } from './models/imageFile';

export const tooltipFactory = {
  showDelay: localStorage.getItem('tooltipDelay'),
  hideDelay: 0,
  touchendHideDelay: 1000
};

@Injectable({
  providedIn: 'root'
})
export class AppGlobalsService {


  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  public triggerContactRequestEmitter = new EventEmitter<{title: string, data: any}>();
  public triggerJoinNowFormEmitter = new EventEmitter<any>();

  public currentLoggedInUser: Observable<IUserAccountModel>;
  public currentLoggedInContact: Observable<ContactModel>;
  public callDashboardCustomizationsModal: Subject<boolean> = new Subject<boolean>();
  public readonly minimumPwdScore: PasswordScoreEnum;
  public readonly minimumPwdLength: number;

  public allSubscriptionPlans: LU_SubscriptionPriceModel_ext[] = [];


  private _appTitle = 'Research Arsenal';
  private _versionBeta: boolean = environment.testMode;
  private _versionSuffix = (this._versionBeta ? 'beta - ' : ' - ') + environment.packageDate;
  private _version: string = environment.VERSION;

  constructor() {
    this.minimumPwdScore = PasswordScoreEnum.Medium;
    this.minimumPwdLength = 8;
  }

  public get appTitle(): string {
    return this._appTitle;
  }
  public get appVersion(): string {
    return this._version = 'v. ' + this._version + ' ' + this._versionSuffix;
  }

  public get dropdownTypeDebounceTime() {
    return 750;
  } // ms

  public getStoredDashOrder(): { modules: string[]; confs: DashConfSave[] } {
    const confs = this.loadData<DashConfSave[]>('ra-dashConfs');
    const modules = this.loadData<string[]>('ra-dashModules');
    return { modules, confs };
  }
  public setDashOrder(data: { modules: string[]; confs: DashConfSave[] }) {
    this.storeData(data.confs, 'ra-dashConfs');
    this.storeData(data.modules, 'ra-dashModules');
  }

  public getStoredTooltipDelay(): number {
    const delay = this.loadData<number>('tooltipDelay');
    return delay;
  }
  public setTooltipDelay(delay: number): void {
    this.storeData(delay, 'tooltipDelay');
  }

  public getStoredBirthdayNoticeSetting(): boolean {
    return this.loadData('bdayNotice');
  }
  public setBirthdayNoticeSetting(set: boolean) {
    this.storeData(set, 'bdayNotice');
  }
  public toggleBirthdayNoticeSetting(): boolean {
    const s = this.getStoredBirthdayNoticeSetting();
    this.setBirthdayNoticeSetting(!s);
    return !s;
  }

  public getStoredTimeZone(): Observable<TimeZone> {
    return of(this.loadData<TimeZone>('timeZone')).pipe(share());
  }
  public getStoredDST(): Observable<boolean> {
    return of(this.loadData<boolean>('DST')).pipe(share());
  }
  public setTimeZone(zone: TimeZone, dst = false): void {
    // console.debug('setting tz' + zone.name);
    this.storeData(zone, 'timeZone');
    this.storeData(dst, 'DST');
  }

  public setDashTheme(theme: { name: string; class: string; color: string }): void {
    this.storeData(theme, 'dashTheme');
  }
  public getDashTheme(): { name: string; class: string; color: string } {
    return this.loadData('dashTheme');
  }

  public setDashFontSize(size: 1 | 2 | 3 | 4 | 5): void {
    this.storeData(size, 'dashFontSize');
  }
  public getDashFontSize(): 1 | 2 | 3 | 4 | 5 {
    return this.loadData('dashFontSize');
  }

  public getStoredDarkModeSetting(): boolean {
    return this.loadData<boolean>('darkMode');
  }
  public setDarkMode(set: boolean): void {
    this.storeData(set, 'darkMode');
  }

  public getStoredGlassModeSetting(): boolean {
    return this.loadData<boolean>('glassMode');
  }
  public setGlassMode(set: boolean): void {
    this.storeData(set, 'glassMode');
  }

  public getStoredNotificationStyle(): number {
    return this.loadData<number>('notificationStyle');
  }
  public setNotificationStyle(style: number): void {
    this.storeData(style, 'notificationStyle');
  }

  public getStoredDashConf<T extends IDashModuleBase<any>>(dashName: string): DashModuleBase<any> {
    const confs = this.loadData<DashModuleBase<any>[]>('ra-dashModules');
    if (confs && confs.length) {
      return confs.find(d => d.boxTitle === dashName) as DashModuleBase<any>;
    } else {
      return null;
    }
  }

  public setDashConf(data: DashModuleBase<any>): void {
    const confs = this.loadData<DashModuleBase<any>[]>('ra-dashConfs');
    const existing = confs.findIndex(d => d.boxTitle === data.boxTitle);
    if (existing !== -1) {
      confs[existing] = data;
      this.storeData(confs, 'ra-dashConfs');
    }
  }

  public clearAll(): boolean {
    return this.deleteData('ra-dashConfs')
      && this.deleteData('ra-dashModules')
      && this.deleteData('ra-newDashConfs')
      && this.deleteData('dashTheme')
      && this.deleteData('timeZone')
      && this.deleteData('DST')
      && this.deleteData('tooltipDelay')
      && this.deleteData('darkMode')
      && this.deleteData('glassMode')
      && this.deleteData('notificationStyle')
      && this.deleteData('bdayNotice')
      && this.deleteData('dashFontSize');
  }

  public openContactRequest(title: string, data = null) {
    this.triggerContactRequestEmitter.emit({title, data});
  }

  public openJoinNowForm(): void {
    this.triggerJoinNowFormEmitter.emit();
  }

  private storeData(data: any, location: string) {
    try {
      localStorage.setItem(location, JSON.stringify(data));
    } catch (e) {
      console.error(e);
      throw new Error('Could not write to browser local storage.');
    }
  }
  private loadData<T>(key: string): T {
    try {
      return JSON.parse(localStorage.getItem(key)) as T;
    } catch (e) {
      console.error(e);
      throw new Error('Could not access browser local storage.');
    }
  }
  private deleteData(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  public setStoredSearch(info: any){
    localStorage.setItem('storedSearch', JSON.stringify(info));
  }

  public loadStoredSearch(): FilterParams<CallNumberModel> {
    try {
      return Object.assign(new FilterParams<CallNumberModel>(), JSON.parse(localStorage.getItem('storedSearch')));
    } catch (e) {
      console.error(e);
      throw new Error('Could not access browser local storage.');
    }
  }

  public clearStoredSearch(){
    localStorage.removeItem('storedSearch');
  }

}
